import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

async function apiFetch(path, { token, method = "GET", body } = {}) {
  const res = await fetch(path, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = json?.message || json?.error || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = json;
    throw err;
  }
  return json;
}

export default function LeadInboxItem() {
  const { id } = useParams();
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function load() {
      if (!isAuthenticated) return;
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const json = await apiFetch(`/api/lead-inbox/${id}`, { token });
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, isAuthenticated, getAccessTokenSilently]);

  if (!isAuthenticated) {
    return (
      <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
        <h1 style={{ fontSize: 22, marginBottom: 8 }}>Lead details</h1>
        <p style={{ marginBottom: 16 }}>Log in to view this lead.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #ddd" }}
        >
          Log in
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="/lead-inbox" style={{ color: "#5a20ff", textDecoration: "none" }}>
          ← Back to inbox
        </Link>
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : error ? (
        <div style={{ color: "#900" }}>{error}</div>
      ) : !data?.item ? (
        <div style={{ color: "#666" }}>Not found.</div>
      ) : (
        <>
          <h1 style={{ fontSize: 26, marginBottom: 6 }}>{data.item.person_name || "(no name)"}</h1>
          <div style={{ color: "#444" }}>
            {(data.item.person_title || "") + (data.item.company_name ? ` @ ${data.item.company_name}` : "")}
          </div>

          {data.item.why_now && (
            <div style={{ marginTop: 14, padding: 12, borderRadius: 12, background: "#f7f7ff" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#5a20ff" }}>WHY NOW</div>
              <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{data.item.why_now}</div>
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#666" }}>PROOF</div>
            <pre style={{ marginTop: 6, padding: 12, border: "1px solid #eee", borderRadius: 12, overflowX: "auto" }}>
              {JSON.stringify(data.item.proof || null, null, 2)}
            </pre>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#666" }}>DRAFTS</div>
            {data.drafts?.length ? (
              data.drafts.map((d) => (
                <div key={d.id} style={{ marginTop: 8, padding: 12, border: "1px solid #eee", borderRadius: 12 }}>
                  <div style={{ fontWeight: 700 }}>Subject: {d.subject || "(none)"}</div>
                  <pre style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{d.body}</pre>
                </div>
              ))
            ) : (
              <div style={{ color: "#666", marginTop: 6 }}>No drafts yet.</div>
            )}
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#666" }}>FEEDBACK</div>
            {data.feedback?.length ? (
              <div style={{ marginTop: 6, color: "#333" }}>
                {data.feedback.slice(0, 10).map((f) => (
                  <div key={f.id} style={{ padding: "6px 0", borderBottom: "1px solid #f3f3f3" }}>
                    <b>{f.verdict}</b> — {new Date(f.created_at).toLocaleString()} ({f.reason || ""})
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#666", marginTop: 6 }}>No feedback yet.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
